import React, { memo } from "react";
import { Text, View, Page, Document, PDFViewer } from "@react-pdf/renderer";
import "../pdf/Fonts.js";

const MaleAnimalTreePdf = ({
    selectedOwner,
    selectedAnimal,
    fetchedData,
}) => {
    // Inline styles
    const styles = {
        pdfViewer: { width: '85vw', height: '100vh' },
        page: {
            padding: 20,
            fontFamily: 'Roboto',
            backgroundColor: 'white'
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: 15
        },
        header: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 15,
            backgroundColor: '#000000',
            padding: 15,
            borderRadius: 5
        },
        title: {
            fontSize: 18,
            fontWeight: 'bold',
            color: 'white'
        },
        sectionHeader: {
            backgroundColor: '#000000',
            color: 'white',
            fontSize: 14,
            fontWeight: 'bold',
            padding: 8,
            marginBottom: 10,
            borderRadius: 3
        },
        familyBox: {
            border: '1pt solid #000',
            borderLeft: '3pt solid #000',
            padding: 10,
            marginBottom: 10,
            borderRadius: 5,
            backgroundColor: '#F5F5F5'
        },
        subheading: {
            fontSize: 14,
            fontWeight: 'bold',
            marginBottom: 5,
            color: '#000000',
            backgroundColor: '#EEEEEE',
            padding: 5,
            borderRadius: 3
        },
        normalText: {
            fontSize: 10,
            marginBottom: 3
        },
        infoValue: {
            fontSize: 10,
            fontWeight: 'normal'
        },
        groupLabel: {
            fontSize: 12,
            fontWeight: 'bold',
            backgroundColor: '#DDDDDD',
            padding: 5,
            borderRadius: 3,
            marginTop: 5,
            marginBottom: 8
        },
        tableContainer: {
            marginTop: 10,
            marginBottom: 15
        },
        table: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            border: '1pt solid #000',
            borderRadius: 3,
            overflow: 'hidden'
        },
        tableHeader: {
            display: 'flex',
            flexDirection: 'row',
            backgroundColor: '#000000',
            padding: 3
        },
        tableRow: {
            display: 'flex',
            flexDirection: 'row',
            borderBottom: '1pt solid #DDD',
            minHeight: 22
        },
        codeCell: {
            width: '10%',
            fontSize: 8,
            padding: 3,
            textAlign: 'left',
            borderRight: '1pt solid #DDD'
        },
        dateCell: {
            width: '9%',
            fontSize: 8,
            padding: 3,
            textAlign: 'center',
            borderRight: '1pt solid #DDD'
        },
        dataCell: {
            flex: 1,
            fontSize: 8,
            padding: 3,
            textAlign: 'center',
            borderRight: '1pt solid #DDD'
        },
        lastDataCell: {
            flex: 1,
            fontSize: 8,
            padding: 3,
            textAlign: 'center'
        },
        headerCell: {
            flex: 1,
            fontSize: 8,
            padding: 3,
            textAlign: 'center',
            fontWeight: 'bold',
            color: 'white',
            borderRight: '1pt solid #444'
        },
        codeHeaderCell: {
            width: '10%',
            fontSize: 8,
            padding: 3,
            fontWeight: 'bold',
            color: 'white',
            borderRight: '1pt solid #444'
        },
        dateHeaderCell: {
            width: '9%',
            fontSize: 8,
            padding: 3,
            fontWeight: 'bold',
            color: 'white',
            borderRight: '1pt solid #444'
        },
        lastHeaderCell: {
            flex: 1,
            fontSize: 8,
            padding: 3,
            textAlign: 'center',
            fontWeight: 'bold',
            color: 'white'
        },
        noDataText: {
            fontSize: 9,
            color: '#555555'
        },
        noDataRow: {
            display: 'flex',
            flexDirection: 'row',
            borderBottom: '1pt solid #DDD',
            minHeight: 22
        },
        noDataCell: {
            flex: 1,
            fontSize: 8,
            padding: 5,
            color: '#555555',
            textAlign: 'center'
        }
    };

    // Helper to format dates with fallback
    const formatDate = (dateString) => {
        if (!dateString) return "—";
        try {
            return new Date(dateString).toLocaleDateString("el-GR");
        } catch (e) {
            return "—";
        }
    };

    // Function to render a general animal data table
    const renderAnimalDataTable = (title, animals, getMilkData) => {
        if (!animals || animals.length === 0) {
            return (
                <View style={styles.tableContainer}>
                    <Text style={styles.subheading}>{title}</Text>
                    <Text style={styles.noDataText}>Δεν υπάρχουν διαθέσιμα στοιχεία</Text>
                </View>
            );
        }

        // Sort animals by registrationCode in ascending order
        const sortedAnimals = [...animals].sort((a, b) => {
            const codeA = a.registrationCode || '';
            const codeB = b.registrationCode || '';
            return codeA.localeCompare(codeB);
        });

        // Create rows array from animals and their milk data
        const rows = [];

        sortedAnimals.forEach(animal => {
            const milkData = getMilkData ? getMilkData(animal) : null;

            if (!milkData || milkData.length === 0) {
                // Add a single row with no milk data message
                rows.push({
                    animalId: animal._id,
                    registrationCode: animal.registrationCode,
                    birthDate: animal.birthDate,
                    lambingDate: null,
                    hasMilkData: false
                });
            } else {
                // Sort milkData by lambingDate in ascending order
                const sortedMilkData = [...milkData].sort((a, b) => {
                    const dateA = a.lambingDate ? new Date(a.lambingDate) : new Date(0);
                    const dateB = b.lambingDate ? new Date(b.lambingDate) : new Date(0);
                    return dateA - dateB;
                });

                // Add a row for each lambing record
                sortedMilkData.forEach(lambing => {
                    rows.push({
                        animalId: animal._id,
                        registrationCode: animal.registrationCode,
                        birthDate: animal.birthDate,
                        lambingDate: lambing.lambingDate,
                        lambingData: {
                            maleAlive: lambing.maleAlive || 0,
                            femaleAlive: lambing.femaleAlive || 0,
                            maleDead: lambing.maleDead || 0,
                            femaleDead: lambing.femaleDead || 0,
                            milkRecordingsCount: lambing.milkRecordings.length,
                            firstMilk: lambing.milkRecordings[0]?.checks?.[0]?.milk,
                            mostMilk: lambing.mostMilk,
                            medianMilk: lambing.medianMilk,
                            firstDay: lambing.firstDay,
                            mostDay: lambing.mostDay,
                            duration: Math.round(lambing.duration),
                            milk: lambing.milk,
                            standardMilk: lambing.standardMilk,
                        },
                        hasMilkData: true
                    });
                });
            }
        });

        return (
            <View style={styles.tableContainer}>
                <Text style={styles.subheading}>{title}</Text>
                <View style={styles.table}>
                    {/* Table header */}
                    <View style={styles.tableHeader}>
                        <Text style={styles.codeHeaderCell}>Κωδικός</Text>
                        <Text style={styles.dateHeaderCell}>Ημ/νία Τοκετού</Text>
                        <Text style={styles.headerCell}>Ζωντανά Αρσενικά</Text>
                        <Text style={styles.headerCell}>Ζωντανά Θηλυκά</Text>
                        <Text style={styles.headerCell}>Νεκρά Αρσενικά</Text>
                        <Text style={styles.headerCell}>Νεκρά Θηλυκά</Text>
                        <Text style={styles.headerCell}>Αριθμός ελέγχων</Text>
                        <Text style={styles.headerCell}>Πρώτος έλεγχος</Text>
                        <Text style={styles.headerCell}>Μέγιστος έλεγχος</Text>
                        <Text style={styles.headerCell}>Μέγιστη / Μέση ποσότητα</Text>
                        <Text style={styles.lastHeaderCell}>Διάρκεια γαλ/γής</Text>
                        <Text style={styles.lastHeaderCell}>Μέση Εμπορεύσιμη Γαλακτοπαραγωγή (χγρ.)</Text>
                        <Text style={styles.lastHeaderCell}>Γαλ/γή Τυπικής Διάρκειας</Text>
                    </View>

                    {/* Table rows */}
                    {rows.map((row, index) => {
                        if (!row.hasMilkData) {
                            return (
                                <View key={`${row.animalId}_${index}`} style={{
                                    ...styles.noDataRow,
                                    backgroundColor: index % 2 === 0 ? '#F8F9FA' : '#FFFFFF'
                                }}>
                                    <Text style={styles.codeCell}>{row.registrationCode || "—"}</Text>
                                    <Text style={styles.dateCell}>{formatDate(row.birthDate)}</Text>
                                    <Text style={{ ...styles.noDataCell, flex: 9 }}>Ο τοκετός δεν έχει ελέγχους</Text>
                                </View>
                            );
                        }

                        return (
                            <View key={`${row.animalId}_${index}`} style={{
                                ...styles.tableRow,
                                backgroundColor: index % 2 === 0 ? '#F8F9FA' : '#FFFFFF'
                            }}>
                                {console.log("LAMBING DATA HERERERERE", row.lambingData)}
                                <Text style={styles.codeCell}>{row.registrationCode || "—"}</Text>
                                <Text style={styles.dateCell}>{formatDate(row.lambingDate)}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.maleAlive}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.femaleAlive}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.maleDead}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.femaleDead}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.milkRecordingsCount}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.firstDay || "—"}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.mostDay || "—"}</Text>
                                <Text style={styles.dataCell}>{`${row.lambingData.mostMilk}/${row.lambingData.medianMilk}` || "—"}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.duration || "—"}</Text>
                                <Text style={styles.dataCell}>{row.lambingData.milk || "—"}</Text>
                                <Text style={styles.lastDataCell}>{row.lambingData.standardMilk || "—"}</Text>
                            </View>
                        );
                    })}
                </View>
            </View>
        );
    };

    return (
        <>
            <PDFViewer style={styles.pdfViewer}>
                <Document>
                    <Page size='A2' style={styles.page} wrap>
                        <View style={styles.container}>
                            {/* Title Page */}
                            <View style={styles.header}>
                                <Text style={styles.title}>Γενεαλογικό δέντρο κριού {selectedAnimal?.registrationCode}</Text>
                            </View>

                            {/* Owner Information */}
                            <View style={styles.familyBox}>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Μονάδα: </Text>
                                    <Text style={styles.infoValue}>{`${selectedOwner?.lastName} ${selectedOwner?.firstName}` || "—"}</Text>
                                </Text>
                            </View>

                            {/* Father's Information */}
                            <View style={styles.familyBox}>
                                <Text style={styles.subheading}>Στοιχεία Πατέρα:</Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Κωδικός Γεν. Βιβλίου: </Text>
                                    <Text style={styles.infoValue}>{fetchedData?.father?.registrationCode || "—"}</Text>
                                </Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Ημερομηνία Γέννησης: </Text>
                                    <Text style={styles.infoValue}>{formatDate(fetchedData?.father?.birthDate)}</Text>
                                </Text>
                            </View>

                            {/* Mother's Information */}
                            <View style={styles.familyBox}>
                                <Text style={styles.subheading}>Στοιχεία Μητέρας:</Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Κωδικός Γεν. Βιβλίου: </Text>
                                    <Text style={styles.infoValue}>{fetchedData?.mother?.registrationCode || "—"}</Text>
                                </Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Ημερομηνία Γέννησης: </Text>
                                    <Text style={styles.infoValue}>{formatDate(fetchedData?.mother?.birthDate)}</Text>
                                </Text>
                                {/* Mother's Milk Data */}
                                {fetchedData?.mother && (
                                    renderAnimalDataTable(
                                        "Δεδομένα Γαλακτοπαραγωγής Μητέρας",
                                        [fetchedData.mother],
                                        (animal) => fetchedData?.femaleFamilyMilkData[animal._id]?.milkData
                                    )
                                )}
                            </View>


                            {/* Grandparents Information */}
                            <View style={styles.familyBox}>
                                <Text style={styles.subheading}>Πληροφορίες Πατέρα Πατέρα:</Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Κωδικός Γεν. Βιβλίου: </Text>
                                    <Text style={styles.infoValue}>{fetchedData?.paternalGrandfather?.registrationCode || "—"}</Text>
                                </Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Ημερομηνία Γέννησης: </Text>
                                    <Text style={styles.infoValue}>{formatDate(fetchedData?.paternalGrandfather?.birthDate)}</Text>
                                </Text>
                            </View>

                            <View style={styles.familyBox}>
                                <Text style={styles.subheading}>Πληροφορίες Μητέρας Πατέρα:</Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Κωδικός Γεν. Βιβλίου: </Text>
                                    <Text style={styles.infoValue}>{fetchedData?.paternalGrandmother?.registrationCode || "—"}</Text>
                                </Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Ημερομηνία Γέννησης: </Text>
                                    <Text style={styles.infoValue}>{formatDate(fetchedData?.paternalGrandmother?.birthDate)}</Text>
                                </Text>
                                {/* Paternal Grandmother's Milk Data */}
                                {fetchedData?.paternalGrandmother && (
                                    renderAnimalDataTable(
                                        "Δεδομένα Γαλακτοπαραγωγής Μητέρας Πατέρα",
                                        [fetchedData.paternalGrandmother],
                                        (animal) => fetchedData?.femaleFamilyMilkData[animal._id]?.milkData
                                    )
                                )}
                            </View>


                            <View style={styles.familyBox}>
                                <Text style={styles.subheading}>Πληροφορίες Πατέρα Μητέρας:</Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Κωδικός Γεν. Βιβλίου: </Text>
                                    <Text style={styles.infoValue}>{fetchedData?.maternalGrandfather?.registrationCode || "—"}</Text>
                                </Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Ημερομηνία Γέννησης: </Text>
                                    <Text style={styles.infoValue}>{formatDate(fetchedData?.maternalGrandfather?.birthDate)}</Text>
                                </Text>
                            </View>

                            <View style={styles.familyBox}>
                                <Text style={styles.subheading}>Πληροφορίες Μητέρας Μητέρας:</Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Κωδικός Γεν. Βιβλίου: </Text>
                                    <Text style={styles.infoValue}>{fetchedData?.maternalGrandmother?.registrationCode || "—"}</Text>
                                </Text>
                                <Text style={styles.normalText}>
                                    <Text style={{ fontWeight: 'bold' }}>Ημερομηνία Γέννησης: </Text>
                                    <Text style={styles.infoValue}>{formatDate(fetchedData?.maternalGrandmother?.birthDate)}</Text>
                                </Text>
                                {/* Maternal Grandmother's Milk Data */}
                                {fetchedData?.maternalGrandmother && (
                                    renderAnimalDataTable(
                                        "Δεδομένα Γαλακτοπαραγωγής Μητέρας Μητέρας",
                                        [fetchedData.maternalGrandmother],
                                        (animal) => fetchedData?.femaleFamilyMilkData[animal._id]?.milkData
                                    )
                                )}
                            </View>

                            {/* Paternal Half Sisters */}
                            {renderAnimalDataTable(
                                "Ετεροθαλείς αδελφές (από την πλευρά του πατέρα)",
                                fetchedData?.sisters?.paternalHalfSisters || [],
                                (animal) => fetchedData?.femaleFamilyMilkData[animal._id]?.milkData
                            )}

                            {/* Maternal Half Sisters */}
                            {renderAnimalDataTable(
                                "Ετεροθαλείς αδελφές (από την πλευρά της μητέρας)",
                                fetchedData?.sisters?.maternalHalfSisters || [],
                                (animal) => fetchedData?.femaleFamilyMilkData[animal._id]?.milkData
                            )}

                            {/* Full Sisters */}
                            {renderAnimalDataTable(
                                "Ομοθαλείς αδελφές",
                                fetchedData?.sisters?.fullSisters || [],
                                (animal) => fetchedData?.femaleFamilyMilkData[animal._id]?.milkData
                            )}

                            {renderAnimalDataTable(
                                "Θυγατέρες",
                                fetchedData?.daughters || [],
                                (animal) => fetchedData?.femaleFamilyMilkData[animal._id]?.milkData
                            )}
                        </View>
                    </Page>
                </Document>
            </PDFViewer>
        </>
    );
};

export default memo(MaleAnimalTreePdf);